import React from 'react';

import ActivitySection from './ActivitySection';
import AISection, {AISectionPlaceholder} from './AISection';
import {
  shouldShowAISection,
  shouldShowMyWorkSection,
  shouldShowWelcomeSection,
  shouldShowWorklistsSection,
} from './HomepageVisibility';
import MyWorkSection, {MyWorkSectionPlaceholder} from './MyWorkSection';
import RecentsSection from './RecentsSection';
import WelcomeSection, {WelcomeSectionPlaceholder} from './WelcomeSection';
import WorklistsSection from './WorklistsSection';

interface HomepageCardDefinitionShape<Id extends string = string> {
  id: Id;
  title: string;
  isFullWidth?: boolean;
  isVisible: () => boolean;
  Card: React.ComponentType;
  Placeholder?: React.ComponentType<HomepageCardPlaceholderProps>;
}

interface HomepageCardPlaceholderProps {
  proxyCardClassName: string;
}

export const HOMEPAGE_CARD_DEFINITIONS = [
  {
    id: 'activity',
    title: 'Running now',
    isVisible: () => true,
    Card: ActivitySection,
  },
  {
    id: 'ai',
    title: 'Start with chat',
    isFullWidth: true,
    isVisible: shouldShowAISection,
    Card: AISection,
    Placeholder: AISectionPlaceholder,
  },
  {
    id: 'myWork',
    title: 'My work',
    isFullWidth: true,
    isVisible: shouldShowMyWorkSection,
    Card: MyWorkSection,
    Placeholder: MyWorkSectionPlaceholder,
  },
  {
    id: 'recents',
    title: 'Jump back in',
    isVisible: () => true,
    Card: RecentsSection,
  },
  {
    id: 'welcome',
    title: 'Explore Benchling',
    isVisible: shouldShowWelcomeSection,
    Card: WelcomeSection,
    Placeholder: WelcomeSectionPlaceholder,
  },
  {
    id: 'worklists',
    title: 'Worklists',
    isVisible: shouldShowWorklistsSection,
    Card: WorklistsSection,
  },
] as const satisfies ReadonlyArray<HomepageCardDefinitionShape>;

export type HomepageCardId = (typeof HOMEPAGE_CARD_DEFINITIONS)[number]['id'];
export type HomepageCardDefinition = HomepageCardDefinitionShape<HomepageCardId>;

export interface HomepageCardDefaultWideLayoutItem {
  id: HomepageCardId;
  col: 0 | 1;
  row: number;
}

export interface HomepageCardDefaultLayouts {
  wide: ReadonlyArray<HomepageCardDefaultWideLayoutItem>;
  narrow: ReadonlyArray<HomepageCardId>;
}

export const DEFAULT_HOMEPAGE_CARD_LAYOUTS: HomepageCardDefaultLayouts = {
  wide: [
    {id: 'myWork', col: 0, row: 0},
    {id: 'ai', col: 0, row: 1},
    {id: 'recents', col: 0, row: 2},
    {id: 'activity', col: 1, row: 2},
    {id: 'worklists', col: 0, row: 3},
    {id: 'welcome', col: 0, row: 4},
  ],
  narrow: ['myWork', 'ai', 'recents', 'worklists', 'welcome', 'activity'],
};

export function getVisibleHomepageCards(): Array<HomepageCardDefinition> {
  return HOMEPAGE_CARD_DEFINITIONS.filter(({isVisible}) => isVisible());
}
